import { FRAMED_SERVICE_SECTION_OPTIONS } from '../../capabilities/service/ServiceBlock';
import type { SkuResourceModulesState } from '../../platform/scene-layout/types';
import { buildDefaultDirectResourceModules } from '../../platform/scene-layout/utils/resolveModuleDefaults';
import type { DeploySceneConfig } from '../../platform/scenes/types';
import { BARE_METAL_MODULES } from './modules';

export const BARE_METAL_ALL_MODULES = BARE_METAL_MODULES.map((m) => m.key);

export type BareMetalModuleKey = (typeof BARE_METAL_ALL_MODULES)[number];

export const BARE_METAL_SKU_MODULES = ['systemSetting', 'bareMetalNodes', 'service'] as const;

export type BareMetalSkuModuleKey = (typeof BARE_METAL_SKU_MODULES)[number];

export const BARE_METAL_SKU_MODULE_LIST: string[] = [...BARE_METAL_SKU_MODULES];

export function isBareMetalSkuModuleKey(moduleKey: string): moduleKey is BareMetalSkuModuleKey {
  return (BARE_METAL_SKU_MODULES as readonly string[]).includes(moduleKey);
}

export function filterBareMetalSkuModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(BARE_METAL_SKU_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

/** 系统配置在网络前：先选 Install/Adopt，再决定是否展示静态 IPv4 */
export const BARE_METAL_DIRECT_MERGED_MODULES = ['baseInfo', 'systemSetting', 'network', 'bareMetalNodes', 'service'] as const;

export type BareMetalDirectMergedModuleKey = (typeof BARE_METAL_DIRECT_MERGED_MODULES)[number];

export function filterBareMetalDirectMergedModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(BARE_METAL_DIRECT_MERGED_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

/** 下单页模块：直建合并去掉 baseInfo */
export const BARE_METAL_ORDER_MODULES = BARE_METAL_DIRECT_MERGED_MODULES.filter(
  (key): key is Exclude<BareMetalDirectMergedModuleKey, 'baseInfo'> => key !== 'baseInfo',
);

export const BARE_METAL_SCENES: Record<'direct' | 'sku' | 'order', DeploySceneConfig> = {
  direct: {
    modules: [...BARE_METAL_ALL_MODULES],
    capabilityOptions: {
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  sku: {
    modules: [...BARE_METAL_SKU_MODULES],
    capabilityOptions: {
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  order: {
    modules: [...BARE_METAL_ORDER_MODULES],
    capabilityOptions: {},
    linkage: true,
  },
};

export function resolveBareMetalSceneConfig(scene?: 'direct' | 'sku' | 'order'): DeploySceneConfig {
  return BARE_METAL_SCENES[scene ?? 'direct'];
}

export interface BareMetalDirectCreateStep {
  key: string;
  title: string;
  modules: string[];
}

export const BARE_METAL_DIRECT_CREATE_STEPS: BareMetalDirectCreateStep[] = [
  { key: 'resourceConfig', title: '资源配置', modules: [...BARE_METAL_DIRECT_MERGED_MODULES] },
];

export function buildBareMetalDirectResourceModulesFromForm(values: Record<string, unknown>): SkuResourceModulesState {
  const defaults = buildDefaultDirectResourceModules('BareMetal');
  const network = values.network as Record<string, unknown> | undefined;
  const hasServices = Array.isArray(network?.service) && (network.service as unknown[]).length > 0;

  return {
    ...defaults,
    ...(hasServices ? { service: true } : {}),
  };
}
